import React, { useState, forwardRef } from 'react';
import SearchSelect from './SearchSelect';

/**
 * StuiSelect - drop-in replacement for <select>, drawn by SearchSelect.
 *
 * Takes <select> markup unchanged: value / defaultValue, onChange(event), disabled, className,
 * style, id, title, onClick, and <option> children (fragments, arrays and conditionals included). The
 * browser's own select popup is never used, because its list ignores the theme and differs by
 * browser. A raw <select> anywhere else fails scripts/check-raw-select.py.
 *
 * Behaves like a native select where code depends on it:
 * - values are strings; onChange receives { target: { value, name, id } } so e.target.value works
 * - an empty value shows the empty option's label (or the first option's) as the placeholder
 * - a value matching no option shows the value itself
 * - an auto-width control is as wide as its longest option, whatever is selected
 *
 * The className keeps its meaning: a trigger carrying .stui-select, .stui-field-input-xs,
 * .stui-ds-cell-input and the like is sized as that control was (STUI.css, "StuiSelect sizes").
 */

const textOf = (node) => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (React.isValidElement(node)) return textOf(node.props.children);
  return '';
};

const collectOptions = (children, out = []) => {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === 'option') {
      const label = textOf(child.props.children);
      out.push({
        id: String(child.props.value ?? label),
        display: label,
        disabled: !!child.props.disabled,
      });
    } else if (child.type === React.Fragment || child.type === 'optgroup') {
      collectOptions(child.props.children, out);
    }
  });
  return out;
};

const StuiSelect = forwardRef(({
  value,
  defaultValue,
  onChange,
  disabled,
  className = '',
  style,
  id,
  name,
  title,
  'aria-label': ariaLabel,
  onClick,
  children,
}, ref) => {
  const [ownValue, setOwnValue] = useState(defaultValue ?? '');
  const controlled = value !== undefined;
  const current = String((controlled ? value : ownValue) ?? '');

  const options = collectOptions(children);
  const match = options.find(o => o.id === current);
  const emptyLabel = (options.find(o => o.id === '') || options[0])?.display ?? '';
  const sizer = options.reduce((a, o) => (o.display.length > a.length ? o.display : a), '');

  const handleChange = (next) => {
    const v = next === null || next === undefined ? '' : String(next);
    if (!controlled) setOwnValue(v);
    if (onChange) {
      const target = { value: v, name, id };
      onChange({ target, currentTarget: target, preventDefault() {}, stopPropagation() {}, persist() {} });
    }
  };

  return (
    <SearchSelect
      ref={ref}
      value={current}
      displayValue={match ? match.display : current}
      options={options}
      onChange={handleChange}
      placeholder={emptyLabel}
      disabled={disabled}
      clearable={false}
      sizer={sizer}
      className={className}
      size={/\bstui-field-input-xs\b/.test(className) ? 'xs' : undefined}
      compactMenu={/\b(stui-select|stui-ds-cell-input|ds-cell-input)\b/.test(className)}
      style={style}
      id={id}
      title={title}
      aria-label={ariaLabel}
      onClick={onClick}
    />
  );
});

StuiSelect.displayName = 'StuiSelect';

export default StuiSelect;
